/**
 * ShipmentsKanbanPage - Logística > Kanban de Remessas (somente leitura).
 *
 * Mesmo esqueleto do Kanban de Propostas: título + filtros rápidos na mesma
 * linha, alternador Lista/Kanban, atualizar e Sheet de filtros avançados.
 * Cards abrem o modal oficial da remessa.
 *
 * RELEASE: 2026-10-16 v1.1.0 - Botão "Nova remessa" no cabeçalho (padrão Kanban de Tarefas).
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, LayoutGrid, List, Plus, RefreshCw, Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { ShipmentKanbanBoard } from '@/components/logistics/kanban/ShipmentKanbanBoard';
import { ShipmentDetailsModal } from '@/components/logistics/ShipmentDetailsModal';
import { ShipmentFormDialog } from '@/components/logistics/ShipmentFormDialog';
import type { ShipmentKanbanFilters } from '@/hooks/useShipments';

type DateField = NonNullable<ShipmentKanbanFilters['dateField']>;
const DATE_FIELDS: { value: DateField; label: string }[] = [
  { value: 'created_at', label: 'Criada em' },
  { value: 'shipped_at', label: 'Enviada em' },
  { value: 'delivered_at', label: 'Entregue em' },
  { value: 'expected', label: 'Previsão de entrega' },
];

export default function ShipmentsKanbanPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  // v1.1.0: mesma checagem da listagem — só cria quem tem permissão em Remessas
  const { canCreate, isAdmin } = usePermissionsContext();
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [direction, setDirection] = useState<'all' | 'outbound' | 'return'>('all');
  const [carrierId, setCarrierId] = useState<string | null>(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dateField, setDateField] = useState<DateField | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  // Busca com atraso curto (limite de 100 caracteres também no servidor)
  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim().slice(0, 100)), 350); return () => clearTimeout(t); }, [search]);

  const filters = useMemo<ShipmentKanbanFilters>(() => ({
    search: debounced, carrierId, overdueOnly,
    direction: direction === 'all' ? null : direction,
    dateField: dateField === 'all' ? null : dateField,
    dateFrom: dateField === 'all' ? null : dateFrom || null,
    dateTo: dateField === 'all' ? null : dateTo || null,
  }), [debounced, carrierId, overdueOnly, direction, dateField, dateFrom, dateTo]);

  const extraCount = dateField !== 'all' ? 1 : 0;
  const hasActive = !!debounced || direction !== 'all' || !!carrierId || overdueOnly || extraCount > 0;
  const clear = () => { setSearch(''); setDirection('all'); setCarrierId(null); setOverdueOnly(false); setDateField('all'); setDateFrom(''); setDateTo(''); };
  const refresh = () => qc.invalidateQueries({ queryKey: ['shipments', currentTenant?.id] });
  const triggerClass = 'h-[22px] px-2 text-xs shrink-0 max-w-[170px] border-0 bg-background rounded-full';

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-1 sm:shrink-0">
          <h1 className="shrink-0 text-xl font-semibold">Kanban de Remessas</h1>
          {/* Busca expansível (padrão Kanban de Propostas) */}
          <div className="hidden items-center md:flex">
            <div className={cn('overflow-hidden transition-all duration-300 ease-in-out', isSearchExpanded ? 'ml-2 mr-1 w-44 opacity-100' : 'w-0 opacity-0')}>
              <Input placeholder="Número, rastreio, destinatário..." value={search} maxLength={100}
                onChange={(e) => setSearch(e.target.value)} onBlur={() => { if (!search) setIsSearchExpanded(false); }}
                className="h-7 w-full rounded-full pl-3 pr-7 text-xs" autoFocus={isSearchExpanded} />
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 rounded-full" title="Buscar remessas"
              onClick={() => setIsSearchExpanded((p) => (p && !search ? false : !p))}>
              {isSearchExpanded ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto scrollbar-hide sm:flex-1 sm:gap-2">
          <Separator orientation="vertical" className="hidden h-5 shrink-0 sm:block" />
          <div className="relative w-40 shrink-0 md:hidden">
            <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Buscar..." value={search} maxLength={100} onChange={(e) => setSearch(e.target.value)} className="h-[22px] w-full rounded-full pl-7 text-xs" />
          </div>
          <Select value={direction} onValueChange={(v) => setDirection(v as typeof direction)}>
            <SelectTrigger className={triggerClass}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="outbound">Envio</SelectItem>
              <SelectItem value="return">Retorno</SelectItem>
            </SelectContent>
          </Select>
          <PersonSelector role="carrier" quickFilter value={carrierId} placeholder="Transportadora" noneLabel="Todas as transportadoras"
            className={cn(triggerClass, 'w-auto')} onSelect={(id) => setCarrierId(id)} />
          <Button variant="outline" size="sm" onClick={() => setOverdueOnly((v) => !v)}
            className={cn(triggerClass, 'gap-1', overdueOnly && 'bg-destructive/10 text-destructive')}>
            <AlertTriangle className="h-3 w-3" />Atrasadas
          </Button>
          <Button variant="outline" size="sm" className={cn(triggerClass, 'gap-1.5')} onClick={() => setIsMoreOpen(true)}>
            <SlidersHorizontal className="h-3 w-3" />Mais
            {extraCount > 0 && <Badge variant="secondary" className="h-4 min-w-[16px] rounded-full border-0 bg-primary/15 px-1 text-[10px] text-primary">{extraCount}</Badge>}
          </Button>
          {hasActive && (
            <Button variant="ghost" size="sm" onClick={clear} className="h-[22px] shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground">
              <X className="mr-1 h-3 w-3" />Limpar
            </Button>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <div className="flex items-center rounded-md border p-0.5">
            <Button variant="ghost" size="icon-sm" onClick={() => navigate('/tenant/logistics/shipments')} title="Visualizar em lista"><List className="h-3.5 w-3.5" /></Button>
            <Button variant="secondary" size="icon-sm" title="Visualizar em kanban por situação"><LayoutGrid className="h-3.5 w-3.5" /></Button>
          </div>
          <Button variant="outline" size="icon-sm" onClick={refresh} title="Atualizar dados"><RefreshCw className="h-3.5 w-3.5" /></Button>
          {/* v1.1.0: criação rápida no cabeçalho, no mesmo lugar e formato do "Nova tarefa" */}
          {(isAdmin || canCreate('log_shipments')) && (
            <Button size="sm" className="h-7 px-2 sm:px-3" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Nova remessa</span>
            </Button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <ShipmentKanbanBoard filters={filters} onOpen={setOpenId} />
      </div>

      <Sheet open={isMoreOpen} onOpenChange={setIsMoreOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Filtros avançados</SheetTitle>
            <SheetDescription>Refine as remessas exibidas no Kanban</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Período por</Label>
              <Select value={dateField} onValueChange={(v) => setDateField(v as DateField | 'all')}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as datas</SelectItem>
                  {DATE_FIELDS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {dateField !== 'all' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">De</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-sm" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Até</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-sm" /></div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* v1.1.0: formulário oficial de remessa (mesmo da listagem); ao salvar, abre os detalhes */}
      <ShipmentFormDialog open={creating} onOpenChange={setCreating} shipment={null} onSaved={(id) => setOpenId(id)} />

      <ShipmentDetailsModal shipmentId={openId} open={!!openId} onOpenChange={(o) => !o && setOpenId(null)} onOpenShipment={setOpenId} />
    </div>
  );
}
